import { Suspense } from 'react';
import { motion, type Variants } from 'framer-motion';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useAuth } from '@/contexts/AuthContext';
import type { DashboardPageQuery } from '@/__generated__/DashboardPageQuery.graphql';

const QUERY = graphql`
  query DashboardPageQuery {
    restaurants(first: 1000) {
      edges {
        node {
          id
        }
      }
    }
    admins(first: 1000) {
      edges {
        node {
          id
        }
      }
    }
    adminFeatureFlags {
      slug
    }
    adminCapabilities {
      id
    }
  }
`;

function DashboardStats() {
  const data = useLazyLoadQuery<DashboardPageQuery>(QUERY, {});

  const stats = [
    {
      label: 'Restaurantes',
      value: data.restaurants.edges.length,
      description: 'Total cadastrados',
    },
    {
      label: 'Admins',
      value: data.admins.edges.length,
      description: 'Staff da plataforma',
    },
    {
      label: 'Feature Flags',
      value: data.adminFeatureFlags.length,
      description: 'Flags ativas',
    },
    {
      label: 'Capabilities',
      value: data.adminCapabilities.length,
      description: 'Definidas no sistema',
    },
  ];

  const containerVariants = {
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
  };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  };

  return (
    <motion.div
      className="contents"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {stats.map((stat) => (
        <motion.div
          key={stat.label}
          variants={cardVariants}
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
        >
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{stat.label}</p>
          <p className="text-3xl font-display font-bold text-slate-900 mt-1">{stat.value}</p>
          <p className="text-xs text-slate-400 mt-1">{stat.description}</p>
        </motion.div>
      ))}
    </motion.div>
  );
}

function DashboardStatsSkeleton() {
  return (
    <>
      {['Restaurantes', 'Usuários', 'Feature Flags', 'Capabilities'].map((label) => (
        <div key={label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-display font-bold text-slate-900 mt-1">—</p>
        </div>
      ))}
    </>
  );
}

export function DashboardPage() {
  const { user } = useAuth();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Bem-vindo, {user?.name}. Gerencie a plataforma LeCartte.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Suspense fallback={<DashboardStatsSkeleton />}>
          <DashboardStats />
        </Suspense>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <h2 className="font-display font-semibold text-slate-900 mb-3">Ações rápidas</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Gerenciar Funcionalidades', href: '/funcionalidades' },
            { label: 'Ver Usuários', href: '/usuarios' },
          ].map((action) => (
            <a
              key={action.href}
              href={action.href}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-success-50 border border-slate-200 hover:border-success-200 text-slate-700 hover:text-success-700 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors"
            >
              {action.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
